// Персональные лимиты пользователя: пустое поле — значение по умолчанию.
// Применяет их сервер (server/limits.mjs); здесь только форма.
import { useState, type ReactElement } from 'react';
import { useT } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError, UserLimits } from '../auth';
import { Modal } from '../ui/shell/Modal';
import { patchUser } from './api';
import type { AdminUser } from './api';
import styles from './LimitsModal.module.css';

export interface LimitsModalProps {
  user: AdminUser;
  onClose(): void;
  /** Список пользователей надо перечитать: лимиты — часть строки. */
  onSaved(): void;
}

/** Поля формы и допустимые пределы — те же, что в server/limits.mjs. */
const FIELDS = [
  { key: 'maxVariants', min: 1, max: 12 },
  { key: 'aiDaily', min: 1, max: 100000 },
  { key: 'magicDaily', min: 1, max: 100000 },
  { key: 'maxTrips', min: 1, max: 1000 },
] as const;
type FieldKey = (typeof FIELDS)[number]['key'];

export function LimitsModal({ user, onClose, onSaved }: LimitsModalProps): ReactElement {
  const t = useT();
  const [values, setValues] = useState<Record<FieldKey, string>>(() => {
    const initial = {} as Record<FieldKey, string>;
    for (const f of FIELDS) {
      const current = user.limits?.[f.key];
      initial[f.key] = current != null ? String(current) : '';
    }
    return initial;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function handleSave() {
    // От сохранённых лимитов, а не с нуля: здесь правятся только числовые
    // поля, а флаги вроде chatFiles живут в том же объекте и должны уцелеть.
    const limits: UserLimits = { ...(user.limits ?? {}) };
    for (const f of FIELDS) {
      const raw = values[f.key].trim();
      if (raw === '') {
        delete limits[f.key];
        continue;
      }
      limits[f.key] = Math.min(f.max, Math.max(f.min, Math.round(Number(raw) || f.min)));
    }
    setBusy(true);
    const res = await patchUser(user.id, {
      limits: Object.keys(limits).length > 0 ? limits : null,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={t('admin.limits.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('form.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void handleSave()}>
            {t('form.save')}
          </button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.user}>{user.email}</p>
        <p className={styles.hint}>{t('admin.limits.hint')}</p>

        {FIELDS.map((f) => (
          <label key={f.key} className={styles.field}>
            <span className="field-label">{t(`admin.limits.${f.key}`)}</span>
            <input
              className={`input ${styles.num}`}
              type="number"
              min={f.min}
              max={f.max}
              value={values[f.key]}
              placeholder={t('admin.limits.default')}
              onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
            />
          </label>
        ))}

        {error && (
          <p className={styles.error} role="alert">
            {t(errorKey(error))}
          </p>
        )}
      </div>
    </Modal>
  );
}
